((root, factory) => {
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.CorkopiaProductExperience = api;
  if (typeof document !== 'undefined') api.init(document);
})(typeof globalThis !== 'undefined' ? globalThis : window, (root) => {
  'use strict';

  const CARD_SELECTOR = '[data-cork-product-card]';
  const QUICK_VIEW_ID = 'ProductQuickView';
  const enhancedCards = new WeakSet();
  const hoveredCards = new WeakSet();
  const quickViewGalleries = new WeakMap();
  const swipeStarts = new WeakMap();

  const text = (value) => value === null || value === undefined ? '' : String(value);
  const titleCase = (value) => text(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const measurementRows = (product, model = root.CorkopiaProductModel) => {
    const measurements = product?.measurements || {};
    const format = (value) => model?.formatMeasurement?.(value) || '';
    const formatWeight = (value) => model?.formatWeight?.(value) || format(value);
    const dimensions = [
      measurements.maximumLength,
      measurements.maximumWidth,
      measurements.maximumHeight
    ].map(format).filter(Boolean);
    const opening = measurements.openingDiameter
      ? format(measurements.openingDiameter)
      : [measurements.openingWidth, measurements.openingHeight].map(format).filter(Boolean).join(' × ');
    return [
      dimensions.length ? ['Measurements', dimensions.join(' × ')] : null,
      measurements.weight ? ['Weight', formatWeight(measurements.weight)] : null,
      opening ? ['Opening', opening] : null
    ].filter(Boolean);
  };

  const discoveryFacts = (product, model = root.CorkopiaProductModel) => {
    const measurements = product?.measurements || {};
    const format = (value) => model?.formatMeasurement?.(value) || '';
    const weight = model?.formatWeight?.(measurements.weight) || format(measurements.weight);
    const length = format(measurements.maximumLength);
    const width = format(measurements.maximumWidth);
    const height = format(measurements.maximumHeight);
    const opening = measurements.openingDiameter
      ? format(measurements.openingDiameter)
      : [measurements.openingWidth, measurements.openingHeight].map(format).filter(Boolean).join(' × ');
    const footprint = [length, width].filter(Boolean).join(' × ');
    const family = product?.classification?.family || '';
    const definitions = {
      cork_round: {
        mode: 'opening',
        title: 'Look through the opening',
        facts: [['Opening', opening], ['Length', length], ['Weight', weight]]
      },
      half_round: {
        mode: 'opening',
        title: 'Read the natural channel',
        facts: [['Opening', opening], ['Length', length], ['Weight', weight]]
      },
      cork_flat: {
        mode: 'dimensions',
        title: 'Measured edge to edge',
        facts: [['Footprint', footprint], ['Thickness', format(measurements.thickness) || height], ['Weight', weight]]
      },
      branch: {
        mode: 'direction',
        title: 'Follow the climbing line',
        facts: [['Longest span', length], ['Branch diameter', format(measurements.branchDiameter) || width], ['Weight', weight]]
      },
      background_piece: {
        mode: 'coverage',
        title: 'Picture the coverage',
        facts: [['Coverage face', footprint], ['Surface', format(measurements.surfaceArea)], ['Depth', height]]
      },
      statement_piece: {
        mode: 'silhouette',
        title: 'Study the silhouette',
        facts: [['Maximum span', length], ['Profile', [width, height].filter(Boolean).join(' × ')], ['Weight', weight]]
      }
    };
    const definition = definitions[family] || {
      mode: 'dimensions',
      title: 'Inspect the exact form',
      facts: [['Dimensions', [length, width, height].filter(Boolean).join(' × ')], ['Weight', weight]]
    };
    return {
      ...definition,
      facts: definition.facts.filter(([, value]) => value)
    };
  };

  const approvedLabels = (product, config = root.CorkopiaProductConfig, model = root.CorkopiaProductModel) => {
    const compatibility = product?.compatibility || {};
    const definitions = config?.compatibility || {};
    const entries = [
      [compatibility.humidity, definitions.humidity],
      [compatibility.buildRoles, definitions.buildRole]
    ];
    if (definitions.approved === true) {
      entries.unshift(
        [compatibility.animals, definitions.animal],
        [compatibility.enclosures, definitions.enclosure]
      );
    }
    return [...new Set(entries.flatMap(([values, definition]) => {
      if (!Array.isArray(values)) return [];
      return model?.compatibilityLabels
        ? model.compatibilityLabels(values, definition, false)
        : values.map(titleCase);
    }).filter(Boolean))];
  };

  const renderLabels = (list, labels) => {
    if (!list) return;
    list.replaceChildren(...labels.map((label) => {
      const item = list.ownerDocument.createElement('li');
      item.textContent = label;
      return item;
    }));
    list.hidden = labels.length === 0;
  };

  const renderRows = (list, rows) => {
    if (!list) return;
    list.replaceChildren(...rows.map(([label, value]) => {
      const row = list.ownerDocument.createElement('div');
      const term = list.ownerDocument.createElement('dt');
      const definition = list.ownerDocument.createElement('dd');
      term.textContent = label;
      definition.textContent = value;
      row.append(term, definition);
      return row;
    }));
  };

  const renderDiscovery = (card, product) => {
    const overlay = card?.querySelector('[data-card-discovery]');
    if (!overlay) return;
    const discovery = discoveryFacts(product);
    overlay.dataset.mode = discovery.mode;
    overlay.querySelector('[data-card-discovery-title]').textContent = discovery.title;
    const facts = overlay.querySelector('[data-card-discovery-facts]');
    facts.replaceChildren(...discovery.facts.map(([label, value]) => {
      const line = facts.ownerDocument.createElement('span');
      const key = facts.ownerDocument.createElement('small');
      const detail = facts.ownerDocument.createElement('b');
      key.textContent = label;
      detail.textContent = value;
      line.append(key, detail);
      return line;
    }));
    overlay.hidden = discovery.facts.length === 0;
  };

  const copyAddContract = (source, destination) => {
    if (!destination) return false;
    [...destination.attributes]
      .filter((attribute) => attribute.name.startsWith('data-') && attribute.name !== 'data-quick-view-add')
      .forEach((attribute) => destination.removeAttribute(attribute.name));
    if (!source) {
      destination.hidden = true;
      destination.disabled = true;
      return false;
    }
    Object.entries(source.dataset).forEach(([key, value]) => { destination.dataset[key] = value; });
    destination.setAttribute('data-cork-add', '');
    destination.hidden = false;
    destination.disabled = source.disabled;
    destination.textContent = source.textContent.trim();
    return true;
  };

  const productDetail = (card) => ({
    product_id: card?.corkopiaProduct?.identity?.shopifyProductId || '',
    variant_id: card?.corkopiaProduct?.identity?.shopifyVariantId || '',
    piece_id: card?.corkopiaProduct?.identity?.pieceId || '',
    family: card?.corkopiaProduct?.classification?.family || '',
    surface: card?.closest?.('[data-home-section]')?.dataset.homeSection
      || (card?.closest?.('[data-cork-builder-shop]') ? 'collection' : 'search')
  });

  const emit = (name, detail, documentRef) => root.CorkopiaAnalytics?.emit?.(name, detail, documentRef);

  const familyRoute = (product) => product?.classification?.family
    ? `/collections/all?filter=${encodeURIComponent(product.classification.family)}`
    : '/collections/all';

  const corkprintSeed = (value) => [...text(value)].reduce((total, character) => (
    (total * 31 + character.charCodeAt(0)) % 360
  ), 0);

  const markUnavailable = (card, documentRef) => {
    if (!card) return;
    card.dataset.availabilityState = 'sold';
    card.classList.add('is-sold');
    root.requestAnimationFrame?.(() => card.classList.add('is-collected'));
    card.querySelectorAll('[data-cork-add]').forEach((button) => {
      button.disabled = true;
      button.textContent = 'Collected';
    });
    const soldState = card.querySelector('[data-card-sold-state]');
    const collectedMark = card.querySelector('[data-card-collected-mark]');
    if (soldState) soldState.hidden = false;
    if (collectedMark) collectedMark.hidden = false;
    const similar = card.querySelector('[data-card-similar]');
    if (similar) similar.href = familyRoute(card.corkopiaProduct);
    const dialog = documentRef.getElementById(QUICK_VIEW_ID);
    if (dialog?.dataset.productId === text(card.corkopiaProduct?.identity?.shopifyProductId)) {
      dialog.querySelector('[data-quick-view-sold]').hidden = false;
      dialog.querySelector('[data-quick-view-actions]')?.setAttribute('hidden', '');
      const add = dialog.querySelector('[data-quick-view-add]');
      if (add) add.hidden = true;
    }
  };

  const galleryFor = (product) => {
    const candidates = [product?.media?.heroImage, ...(product?.media?.gallery || [])];
    return candidates
      .map((media) => ({
        url: media?.url || media?.previewUrl || '',
        alt: media?.alt || product?.identity?.title || 'Exact cork piece'
      }))
      .filter((media, index, list) => media.url && list.findIndex((candidate) => candidate.url === media.url) === index);
  };

  const showQuickViewImage = (dialog, requestedIndex, animate = true) => {
    const state = quickViewGalleries.get(dialog);
    if (!state?.items.length) return false;
    const index = (requestedIndex + state.items.length) % state.items.length;
    state.index = index;
    const media = state.items[index];
    const image = dialog.querySelector('[data-quick-view-image]');
    image.src = media.url;
    image.alt = media.alt;
    dialog.querySelector('[data-quick-view-gallery-count]').textContent = `${index + 1} / ${state.items.length}`;
    if (animate && !root.matchMedia?.('(prefers-reduced-motion: reduce)').matches && image.animate) {
      image.animate(
        [{ opacity: .38, transform: 'scale(.992)' }, { opacity: 1, transform: 'scale(1)' }],
        { duration: 280, easing: 'ease-out' }
      );
    }
    return true;
  };

  const stepQuickViewImage = (dialog, direction) => {
    const state = quickViewGalleries.get(dialog);
    return showQuickViewImage(dialog, (state?.index || 0) + direction);
  };

  const populateQuickView = (card, documentRef) => {
    const dialog = documentRef.getElementById(QUICK_VIEW_ID);
    const product = card?.corkopiaProduct;
    if (!dialog || !product) return false;
    const query = (selector) => dialog.querySelector(selector);
    const gallery = galleryFor(product);
    quickViewGalleries.set(dialog, { items: gallery, index: 0 });
    showQuickViewImage(dialog, 0, false);
    const galleryControls = query('[data-quick-view-gallery-controls]');
    const swipeHint = query('[data-quick-view-swipe-hint]');
    galleryControls.hidden = gallery.length < 2;
    swipeHint.hidden = gallery.length < 2;
    query('[data-quick-view-piece-id]').textContent = product.identity.pieceId || '';
    query('[data-quick-view-corkprint-id]').textContent = product.identity.pieceId || 'Exact piece';
    query('[data-quick-view-title]').textContent = product.identity.title || 'Exact cork piece';
    query('[data-quick-view-family]').textContent = product.classification.familyLabel || 'Natural cork';
    query('[data-quick-view-price]').textContent = root.CorkopiaProductModel?.formatPrice?.(
      product.commerce.priceCents,
      product.commerce.currency
    ) || '';
    query('[data-quick-view-one-of-one]').hidden = !product.classification.exactPiece;
    query('[data-quick-view-guarantee]').hidden = !product.classification.exactPiece;
    renderRows(query('[data-quick-view-measurements]'), measurementRows(product));
    renderLabels(query('[data-quick-view-labels]'), approvedLabels(product));
    const full = query('[data-quick-view-full]');
    full.href = card.dataset.productUrl || '#';
    full.dataset.corkopiaLabel = product.identity.pieceId || product.identity.title;
    query('[data-quick-view-similar]').href = familyRoute(product);
    const sourceAdd = card.querySelector('[data-cork-add]');
    copyAddContract(sourceAdd, query('[data-quick-view-add]'));
    const sold = card.dataset.availabilityState === 'sold'
      || ['sold', 'unavailable'].includes(product.availability.state);
    query('[data-quick-view-sold]').hidden = !sold;
    query('[data-quick-view-actions]').hidden = false;
    if (sold) query('[data-quick-view-add]').hidden = true;
    dialog.dataset.productId = product.identity.shopifyProductId || '';
    dialog.dataset.sourceCardPosition = card.dataset.position || '';
    return true;
  };

  const enhanceCard = (card, documentRef) => {
    if (!card || enhancedCards.has(card)) return;
    const product = card.corkopiaProduct;
    if (!product) return;
    enhancedCards.add(card);
    renderLabels(card.querySelector('[data-card-compatibility]'), approvedLabels(product));
    renderDiscovery(card, product);
    card.style.setProperty('--corkprint-turn', `${corkprintSeed(product.identity.pieceId || product.identity.shopifyProductId)}deg`);
    const primaryImage = card.querySelector('.cork-product-card__image--primary');
    const markImageReady = () => card.classList.add('is-image-ready');
    if (primaryImage?.complete && primaryImage.naturalWidth) markImageReady();
    else {
      primaryImage?.addEventListener('load', markImageReady, { once: true });
      primaryImage?.addEventListener('error', markImageReady, { once: true });
    }
    const add = card.querySelector('[data-cork-add]');
    if (add && !product.availability.sellable) {
      add.disabled = true;
      if (['sold', 'unavailable'].includes(product.availability.state)) markUnavailable(card, documentRef);
    }
    card.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch' || hoveredCards.has(card)) return;
      hoveredCards.add(card);
      emit('product_card_hovered', productDetail(card), documentRef);
    });
  };

  const confirmAdd = (source, piece = {}) => {
    const documentRef = source?.ownerDocument || root.document;
    if (!documentRef) return false;
    const variantId = text(piece.variantId || source?.dataset?.variantId);
    const sourceCard = source?.closest?.(CARD_SELECTOR);
    const matchingCard = sourceCard || [...documentRef.querySelectorAll(`${CARD_SELECTOR} [data-cork-add]`)]
      .find((button) => text(button.dataset.variantId) === variantId)
      ?.closest(CARD_SELECTOR);
    const visualRoot = source?.closest?.('[data-quick-view-dialog]');
    const momentTargets = visualRoot ? [visualRoot] : [matchingCard].filter(Boolean);
    momentTargets.forEach((element) => {
      element.classList.add('is-traveling-to-box', 'is-corkprint-stamped');
    });
    root.setTimeout?.(() => momentTargets.forEach((element) => {
      element.classList.remove('is-traveling-to-box');
    }), 660);
    root.setTimeout?.(() => momentTargets.forEach((element) => {
      element.classList.remove('is-corkprint-stamped');
    }), 1500);
    return Boolean(matchingCard || visualRoot);
  };

  const init = (documentRef) => {
    if (documentRef.documentElement?.dataset.productExperienceReady === 'true') return;
    if (documentRef.documentElement) documentRef.documentElement.dataset.productExperienceReady = 'true';

    const enhanceAll = () => documentRef.querySelectorAll(CARD_SELECTOR).forEach((card) => enhanceCard(card, documentRef));
    documentRef.addEventListener('corkopia:product-normalized', (event) => enhanceCard(event.detail?.host, documentRef));
    documentRef.addEventListener('corkopia:products-ready', enhanceAll);

    documentRef.addEventListener('click', (event) => {
      const galleryPrevious = event.target.closest?.('[data-quick-view-previous]');
      const galleryNext = event.target.closest?.('[data-quick-view-next]');
      if (galleryPrevious || galleryNext) {
        event.preventDefault();
        const dialog = event.target.closest('[data-quick-view-dialog]');
        if (dialog) stepQuickViewImage(dialog, galleryPrevious ? -1 : 1);
        return;
      }

      const toggle = event.target.closest?.('[data-card-details-toggle]');
      if (toggle) {
        const card = toggle.closest(CARD_SELECTOR);
        const expanded = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(expanded));
        card?.classList.toggle('is-details-open', expanded);
        const symbol = toggle.querySelector('[aria-hidden]');
        if (symbol) symbol.textContent = expanded ? '−' : '＋';
        return;
      }

      const trigger = event.target.closest?.('[data-quick-view]');
      if (!trigger) return;
      const card = trigger.closest(CARD_SELECTOR);
      if (!populateQuickView(card, documentRef)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (card?.dataset.productUrl) root.location.href = card.dataset.productUrl;
        return;
      }
      emit('product_quick_view_opened', productDetail(card), documentRef);
    }, true);

    documentRef.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch') return;
      const media = event.target.closest?.('.c-quick-view__media');
      if (media) swipeStarts.set(media, event.clientX);
    }, { passive: true });

    documentRef.addEventListener('pointerup', (event) => {
      if (event.pointerType !== 'touch') return;
      const media = event.target.closest?.('.c-quick-view__media');
      const start = media ? swipeStarts.get(media) : null;
      if (!media || !Number.isFinite(start)) return;
      swipeStarts.delete(media);
      const distance = event.clientX - start;
      if (Math.abs(distance) < 44) return;
      const dialog = media.closest('[data-quick-view-dialog]');
      if (dialog) stepQuickViewImage(dialog, distance < 0 ? 1 : -1);
    }, { passive: true });

    documentRef.addEventListener('corkopia:piece-unavailable', (event) => {
      const variantId = text(event.detail?.variantId);
      documentRef.querySelectorAll(CARD_SELECTOR).forEach((card) => {
        if (text(card.corkopiaProduct?.identity?.shopifyVariantId) === variantId) markUnavailable(card, documentRef);
      });
    });

    if (documentRef.readyState === 'loading') documentRef.addEventListener('DOMContentLoaded', enhanceAll);
    else enhanceAll();
  };

  return {
    QUICK_VIEW_ID,
    approvedLabels,
    confirmAdd,
    copyAddContract,
    discoveryFacts,
    galleryFor,
    init,
    measurementRows,
    populateQuickView,
    showQuickViewImage,
    titleCase
  };
});
